﻿import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Spinner, Button, Badge,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../hooks/AuthProvider";
import { useRoleContext } from "../../context/RoleContext";
import useMyRentals from "../../hooks/useMyRentals";
import {
  getPropertyById,
  onPendingInvitations,
  acceptInvitation,
  declineInvitation,
} from "../../helpers/firestoreService";

const TenantMyRentals = () => {
  document.title = "My Rentals | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();
  const { refreshRoles } = useRoleContext();

  const { rentals, loading, reload } = useMyRentals();
  const [invitations, setInvitations] = useState([]); // [{ ...invitation, property }]
  const [invActionLoading, setInvActionLoading] = useState({});

  // Live pending invitations for this email
  useEffect(() => {
    if (!user?.email) return;
    const unsubscribe = onPendingInvitations(user.email, async (invs) => {
      const withProperty = await Promise.all(
        invs.map(async (inv) => ({
          ...inv,
          property: inv.propertyId ? await getPropertyById(inv.propertyId).catch(() => null) : null,
        }))
      );
      setInvitations(withProperty);
    });
    return () => unsubscribe();
  }, [user?.email]);

  const handleAccept = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "accept" }));
    try {
      await acceptInvitation(inv.id, user.uid, user.displayName || "");
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      await refreshRoles();
      await reload();
    } catch (e) {
      console.error("Accept invitation error:", e);
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n; });
    }
  };

  const handleDecline = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "decline" }));
    try {
      await declineInvitation(inv.id);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      await reload();
    } catch (e) {
      console.error("Decline invitation error:", e);
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n; });
    }
  };

  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: 1000 }}>

        <div className="rounded-3 mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 100 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h4 className="mb-1 text-white fw-bold">
              <i className="bx bx-buildings me-2" />My Rentals
            </h4>
            <p className="mb-0 opacity-75 small">
              {rentals.length === 0
                ? "No rental properties linked to your account yet."
                : `${rentals.length} propert${rentals.length === 1 ? "y" : "ies"} linked to your account.`}
            </p>
          </div>
        </div>

        {invitations.length > 0 && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <h6 className="fw-bold mb-3">
                <i className="bx bx-envelope text-warning me-2" />
                Pending Invitations
                <Badge color="warning" pill className="ms-2">{invitations.length}</Badge>
              </h6>
              {invitations.map((inv) => (
                <div
                  key={inv.id}
                  className="d-flex align-items-center justify-content-between gap-3 rounded p-3 mb-2"
                  style={{ background: "#fffbeb" }}
                >
                  <div className="min-w-0">
                    <div className="fw-semibold text-truncate">
                      {inv.property?.address || "A property"}
                    </div>
                    <div className="text-muted small">
                      You&apos;ve been invited as a tenant.
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      color="success"
                      disabled={!!invActionLoading[inv.id]}
                      onClick={() => handleAccept(inv)}
                    >
                      {invActionLoading[inv.id] === "accept" ? <Spinner size="sm" /> : "Accept"}
                    </Button>
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      disabled={!!invActionLoading[inv.id]}
                      onClick={() => handleDecline(inv)}
                    >
                      {invActionLoading[inv.id] === "decline" ? <Spinner size="sm" /> : "Decline"}
                    </Button>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {rentals.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ width: 72, height: 72, background: "#eff2ff" }}
              >
                <i className="bx bx-home-alt" style={{ fontSize: "2rem", color: "#556ee6" }} />
              </div>
              <h5 className="fw-semibold mb-2">No Rentals Found</h5>
              <p className="text-muted small mb-0">
                {invitations.length > 0
                  ? "Accept an invitation above to see the property here."
                  : "You haven't been linked to a property yet. Your property manager will add you once a lease is set up."}
              </p>
            </CardBody>
          </Card>
        ) : (
          <Row className="g-3">
            {rentals.map(({ tenantRecord, property }) => (
              <Col xs={12} md={6} key={tenantRecord.id}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ cursor: "pointer", transition: "box-shadow 0.15s" }}
                  onClick={() => navigate(`/tenant/my-rentals/${tenantRecord.id}`)}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
                >
                  <CardBody className="p-4">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 44, height: 44, background: "#eff2ff" }}
                      >
                        <i className="bx bx-building-house text-primary" style={{ fontSize: "1.3rem" }} />
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <h6 className="fw-bold mb-0 text-truncate">
                          {property?.address || "Loading address..."}
                        </h6>
                        {tenantRecord.name && (
                          <div className="text-muted small">
                            <i className="bx bx-user me-1" />{tenantRecord.name}
                          </div>
                        )}
                      </div>
                      <i className="bx bx-chevron-right text-muted mt-1" />
                    </div>

                    {property?.city && (
                      <div className="text-muted small">
                        <i className="bx bx-map me-1" />{property.city}{property.state ? `, ${property.state}` : ""}
                      </div>
                    )}

                    <Button
                      color="success"
                      size="sm"
                      className="mt-3"
                      onClick={(e) => { e.stopPropagation(); navigate(`/tenant/my-rentals/${tenantRecord.id}?tab=rent`); }}
                    >
                      <i className="bx bx-credit-card me-1" />Pay Rent
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default TenantMyRentals;
